import { Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  LeakDetectionCommands,
  LeakDetectionProperties,
  SaltLevelProperties,
} from '@vi-iot/client';

import { GTMService } from '@services';
import { InstallationFeaturesStore } from '@store';

import { EditFeatureValueModalComponent } from '../../../../shared/components/edit-feature-value-modal/edit-feature-value-modal.component';
import { EditFeatureValueData } from '../../../../shared/models/editFeatureValueData.model';
import { MatTooltip } from '@angular/material/tooltip';
import { matTooltipSetter } from 'src/app/shared/functions/mat-tooltip-setter/mat-tooltip-setter';

@Component({
  selector: 'app-eco-water-overlay',
  templateUrl: './eco-water-overlay.component.html',
  styleUrls: ['./eco-water-overlay.component.scss'],
})
export class EcoWaterOverlayComponent implements OnInit, OnDestroy {
  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }
  readonly featurePropertyKey = FeaturePropertyKey;

  editFeatureValueDialogRef: MatDialogRef<EditFeatureValueModalComponent>;
  saltLevelDays$: Observable<Feature<SaltLevelProperties, never>>;
  leakDetection: Feature<LeakDetectionProperties, LeakDetectionCommands>;
  editFlowValueModalData: EditFeatureValueData;
  editFlowDurationModalData: EditFeatureValueData;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  private commonModalData = {
    title: 'FEATURE-VALUE-EDIT-MODAL.MODAL_TITLE',
    categoryNameForGTM: 'Vitoset Aqua',
    errorText: {
      required: 'FEATURE-VALUE-EDIT-MODAL.ERROR_MSG.REQUIRED',
      min: 'FEATURE-VALUE-EDIT-MODAL.ERROR_MSG.MIN',
      max: 'FEATURE-VALUE-EDIT-MODAL.ERROR_MSG.MAX',
      pattern: 'FEATURE-VALUE-EDIT-MODAL.ERROR_MSG.NUMBERS_PATTERN',
    },
  };

  constructor(
    private dialog: MatDialog,
    private gtmService: GTMService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.saltLevelDays$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ecoWaterSaltLevelDays
    );
    this.installationFeaturesStore
      .selectInstallationDeviceFeature<LeakDetectionProperties, LeakDetectionCommands>(
        FeatureNames.leakDetection
      )
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((leakDetection) => {
        this.leakDetection = leakDetection;
        this.editFlowValueModalData = {
          ...this.commonModalData,
          value: leakDetection.properties?.maxFlow?.value,
          inputSettings: {
            name: '+INSTALLATIONS.INSTALLATION.ECOWATER.FLOW_VALUE',
            actionNameForGTM: 'Flow value',
            type: 'number',
            isRequired: true,
            min: leakDetection.commands.setMaxFlow.params.maxFlow.constraints.min,
            max: leakDetection.commands.setMaxFlow.params.maxFlow.constraints.max,
            pattern: '[0-9]+',
          },
          onSaveProperties: {
            featureName: FeatureNames.leakDetection,
            propertyName: 'maxFlow',
            paramName: '',
            commandName: leakDetection.commands.setMaxFlow.name,
          },
        };

        this.editFlowDurationModalData = {
          ...this.commonModalData,
          value: leakDetection.properties.maxDuration.value,
          inputSettings: {
            name: '+INSTALLATIONS.INSTALLATION.ECOWATER.FLOW_DURATION',
            actionNameForGTM: 'Flow duration',
            type: 'number',
            isRequired: true,
            min: leakDetection.commands.setMaxDuration.params.maxDuration.constraints.min,
            max: leakDetection.commands.setMaxDuration.params.maxDuration.constraints.max,
            pattern: '[0-9]+',
          },
          onSaveProperties: {
            featureName: FeatureNames.leakDetection,
            propertyName: 'maxDuration',
            paramName: '',
            commandName: leakDetection.commands.setMaxDuration.name,
          },
        };
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  openEditModal(isFlowValueEditing: boolean): void {
    const dialogData = isFlowValueEditing
      ? this.editFlowValueModalData
      : this.editFlowDurationModalData;
    this.editFeatureValueDialogRef = this.dialog.open(EditFeatureValueModalComponent, {
      width: '496px',
      disableClose: true,
      data: dialogData,
    });
    this.gtmService.track(
      'onOpen',
      `Edit ${dialogData.inputSettings.actionNameForGTM} dialog`,
      this.commonModalData.categoryNameForGTM
    );
  }
}
